import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useSelector, useDispatch } from 'react-redux'

import { Typography, Button, useDimensions } from '@telavita-core/react-design-kit'

import Logo from 'components/Logo'
import Row from 'components/Row'
import { Column, Container } from 'components'
import { PhoneInput, handleValidPhoneNumber} from 'components/PhoneInput'
import { PinSendedMessage } from 'components/PinSendedMessage'
import { Pin } from 'components/Pin'

import { history } from 'routers/AppRouter'

import { asyncError, authUser } from 'redux/actions'

import axios from 'settings/axios'
import { REGISTER_ATTACH_MEDICAL_REQUEST, REGISTER_SUCCESS, HOME_PSIC, REGISTER_MEDICAL_REQUEST } from 'settings/_routesSettings'
import { apiEndpoints } from 'settings/_apiSettings'
import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'

const ResendPin = () => {

  const dispatch = useDispatch()
  const { isMobile } = useDimensions()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const { ENTERPRISE } = PlanGroupCategoryEnum
  const { ACCOUNTS, PERSONS } = apiEndpoints

  const [cellPhone, setCellPhone] = useState('')
  const [cellPhoneMsg, setCellPhoneMsg] = useState('')
  const [disableInput, setDisableInput] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [pinCode, setPinCode] = useState('')
  const [pinSent, setPinSent] = useState(false)
  const [showPin, setShowPin] = useState(false)
  const [pinError, setPinError] = useState(false)
  const [resendPinLoading, setResendPinLoading] = useState(false)
  const [sendPhoneNumberLoading, setSendPhoneNumberLoading] = useState(false)
  const [finishLoading, setFinishLoading] = useState(false)

  const userData = useSelector(state => state.auth.userData)
  const errors = useSelector(state => state.async.errorList)
  const isEnterprise = useSelector(state => ENTERPRISE === state.auth.planSelected.plan_group.category)
  const usesNewMedicalRequestScreen = useSelector(state =>  state.auth?.planSelected?.uses_new_medical_request_screen)

  const token = useSelector(state => state.auth.userToken)

  const { username } = userData
  const captcha = document.querySelector('.grecaptcha-badge')

  useEffect(() => {
    if(captcha) {
      captcha.style.opacity = '1'
    }
    return () => {
      if(captcha) {
        captcha.style.opacity = '0'
      }
    }
  }, [captcha])

  const handlePinInput = (value) => {
    setIsValid(true)
    setPinCode(value)
  }

  const onCellChange = (cellPhone) => {
    if (cellPhone) {
      if (handleValidPhoneNumber(cellPhone)) {
        setCellPhone(cellPhone)
        setCellPhoneMsg('')
        setIsValid(true)
      } else {
        setCellPhone(cellPhone)
        setCellPhoneMsg('Campo obrigatório.')
        setIsValid(false)
      }
    } else {
      setCellPhone(cellPhone)
      setCellPhoneMsg('Campo obrigatório.')
      setIsValid(false)
    }
  }

  const onEditPhone = () => {
    setCellPhone('')
    setPinError(false)
    setDisableInput(false)
    setIsValid(false)
    setShowPin(false)
  }

  const onNextStep = () => {
    if (isValid) {
      setIsValid(false)
      setSendPhoneNumberLoading(true)

      const { session } = userData
      const { profile_role_active } = session

      const putUrl = `${PERSONS}${username}/?profile_role=${profile_role_active}`

      let clearCellphone = cellPhone.replace('+', '')

      const postData = { cellphone_number: clearCellphone }

      axios.put(putUrl, postData)
        .then(response => {
          if (response.status === 'OK') {
            setCellPhoneMsg('')
            setDisableInput(true)
            handlePin()
          } else {
            setDisableInput(false)
            setIsValid(false)
            setShowPin(false)
          }
        })
        .catch(err => {
          dispatch(asyncError(err)
            .then(() => {
              setCellPhoneMsg(errors?.fieldError
                ? errors?.fieldError[0]?.message
                : '',
              )
              setDisableInput(false)
              setIsValid(false)
              setShowPin(false)
            })
          )
        })
    } else {
      setCellPhoneMsg('Número de telefone inválido!')
    }
  }

  const handlePin = async () => {
    setIsValid(false)
    setPinSent(false)
    setShowPin(false)

    const postUrl = `${ACCOUNTS}${username}/pin/`

    const token = await executeRecaptcha('signup')

    const params = {
      recaptcha_token: token,
    }

    axios.post(postUrl, params)
      .then(response => {
        if (response.status === 'OK') {
          setPinSent(true)
          setShowPin(true)
        }
      })
      .catch(err => {
        dispatch(asyncError(err)
          .then(() => {
            setCellPhoneMsg('O Pin não foi enviado corretamente. Tente novamente.')
            setPinSent(false)
            onEditPhone()
          })
        )
      })
      .finally(() => {
        setSendPhoneNumberLoading(false)
      })
  }

  async function resendPin() {
    try {
      setCellPhoneMsg('')
      setResendPinLoading(true)
      setPinSent(false)
      setPinError(false)
      setPinCode('')
      const postUrl = `${ACCOUNTS}${username}/pin/`
      const token = await executeRecaptcha('signup')
      const params = {
        recaptcha_token: token,
      }

      const response = await axios.post(postUrl, params)

      if (response.status === 'OK') {
        setPinSent(true)
      }

    } catch(error) {
      setCellPhoneMsg('O Pin não foi enviado corretamente. Tente novamente.')
      setPinSent(false)
    } finally {
      setResendPinLoading(false)
    }
  }

  const onSubmit = () => {
    setPinSent(false)
    setFinishLoading(true)
    setPinError(false)

    const getUrl = `${ACCOUNTS}${username}/pin/${pinCode}/`

    axios.get(getUrl)
      .then(async (response) => {
        if (response.status === 'OK') {
          await dispatch(authUser(token))
          if (isEnterprise) {
            if (usesNewMedicalRequestScreen) {
              history.push(REGISTER_MEDICAL_REQUEST)
            } else {
              history.push(REGISTER_ATTACH_MEDICAL_REQUEST)
            }

          }else{
            history.push(REGISTER_SUCCESS)
          }
        }
      })
      .catch(error => {
        const { status } = error.response

        if(status === 404) {
          setPinError(true)
          setPinCode('')
        }
      })
      .finally(() => {
        setFinishLoading(false)
      })
  }

  return (
    <Container isView>
      <Row justify="center" margin="3.7rem 0 3.0rem">
        <Column align="center" justify="center" colWidth={4}>
          <Column colWidth={3}>
            <Logo
              fill="#1f35b5"
              onClickLogo={() => window.location.href = process.env.REACT_APP_INSTITUTIONAL_URL}
            />
          </Column>
        </Column>
      </Row>
      {
        !userData
          ? <Redirect to={HOME_PSIC} />
          : (
            <Row justify="center">
              <Column colWidth={4}>
                <Typography
                  variant={isMobile ? 'header4' : 'header2'}
                  weight='bold'
                  inlineStyles={{
                    marginBottom: '20px',
                  }}
                >
                  Confirmação de cadastro
                </Typography>

                <Typography
                  variant='content2'
                >
                  É preciso ativar seu cadastro para continuar.<br />
                  Insira abaixo seu número de celular e receba o <strong>PIN</strong> de ativação:
                </Typography>

                <Row justify="center" margin='2.5rem 0 0'>
                  <Column>
                    <PhoneInput
                      disabled={disableInput}
                      onChangePhone={cellPhone => onCellChange(cellPhone)}
                      value={cellPhone}
                    />

                    <div className="messageError">
                      <span>{cellPhoneMsg}</span>
                    </div>

                    {
                      showPin ? (
                        <Column>

                          {pinSent || resendPinLoading || pinError
                            ? <PinSendedMessage hasPinError={pinError} resendPinLoading={resendPinLoading} />
                            : null
                          }

                          <Row justify="center" padding="1rem 0">
                            <Pin
                              handlePinInputChange={handlePinInput}
                              onClickEditPhoneNumber={onEditPhone}
                              onClickResendPin={resendPin}
                              resendPinLoading={resendPinLoading}
                              hasPinError={pinError}
                            />

                            <Row padding="2rem 0 0 0">

                              <div className='MessageRequired'>
                                <Typography
                                  italic
                                  variant='content2'
                                  customClassName='MessageRequired--title'
                                >
                                * Preenchimento obrigatório.
                                </Typography>
                              </div>

                              <Button
                                variant='contained'
                                disabled={pinCode.length !== 4 || finishLoading}
                                onClick={onSubmit}
                                uppercase={false}
                                inlineStyles={{
                                  width: '100%',
                                }}
                              >
                                {finishLoading ? 'Finalizando...' : 'Finalizar'}
                              </Button>
                            </Row>
                          </Row>
                        </Column>
                      ) : (
                        <Row align='space-between' direction='row' margin='2.5rem 0 0' wrap='nowrap'>
                          <Column>
                            <Button
                              variant='contained'
                              disabled={!isValid}
                              onClick={onNextStep}
                              uppercase={false}
                              inlineStyles={{
                                width: '100%',
                              }}
                            >
                              {sendPhoneNumberLoading ? 'Enviando... ': 'Enviar'}
                            </Button>
                          </Column>
                        </Row>
                      )
                    }
                  </Column>
                </Row>
              </Column>
            </Row>
          )
      }
    </Container>
  )
}

export default ResendPin
